import { useCallback, useEffect, useRef, useState } from 'react';

import { User as Auth0User, IdToken, useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import queryString from 'query-string';

import {
  Auth0AccessToken,
  Auth0IDToken,
  ErrorMessages,
  MAPPING_NOT_AVAILABLE_ERROR,
  SuccessData,
  User,
} from '@domain/auth';
import { getLoginErrorMessages } from '@domain/auth/getLoginErrorMessages';

import { getAuthenticationError } from '@use-cases/auth';
import { compareClientServerTime } from '@use-cases/login/serverTime';
import { useNotifications } from '@use-cases/notifications';

import {
  authenticateUserBackendApp,
  authenticateUserWebApp,
} from '@repositories/auth/authExternalApps';
import { checkExternalDestinationCurried } from '@repositories/auth/externalDestination';
import { useCreateIndividualFromSession } from '@repositories/auth/hooks';
import { prepHandleLoginDestination } from '@repositories/auth/prepHandleLoginDestination';
import {
  CreateIndividualFromSessionResponse,
  DrupalError,
  DrupalResponse,
} from '@repositories/auth/typings';
import { useBasicInfo } from '@repositories/basicInfo';
import { useMaintenanceModeCheck } from '@repositories/maintenance-mode/get-maintenance-mode';

import {
  FEATURE_AUTH0_LOGIN,
  FEATURE_LOGIN_AUTH_CONTROLLER,
  isEnabled,
} from '@utils/features';
import { getMessageByLoginStatus } from '@utils/getMessageByLoginStatus';
import {
  destinationOrPath,
  isLanguageAvailable,
  localizedNavigate,
} from '@utils/localized-navigate';
import { isUrlAbsolute } from '@utils/url';

import { DOMINO_LOGIN_FLAG, DOMINO_ROUTE_FLAG } from '@domui-domain/constants';
import { useAppConfig as useDomuiAppConfig } from '@domui-hooks/appConfig';
import { useFetchBasicInfo } from '@domui-hooks/useFetchBasicInfo';
import { useFetchDominoMetaConfig } from '@domui-hooks/useFetchDominoMetaConfig';
import { prepHandleLoginDestination as preHandleDominoLoginDestination } from '@domui-repositories/auth/prepHandleLoginDestination';

import { useTranslation } from '@external/react-i18next';
import { useAppConfig } from '@hooks/appConfig';
import { useLogger } from '@hooks/logger';

import { DIS } from '@typings/dis';
import { Status, UserAuthenticate } from '@typings/express-responses';
import { OktaTokenErrors } from '@typings/okta';
import { LogLevel } from '@typings/operations';

const appUrl = process.env.MRX_APP_PUBLIC_URL ?? '';

let errorMessages: ErrorMessages = {};

const useAuth0Hook = (
  accessToken: string,
  idTokenClaims: IdToken | undefined,
  auth0User: Auth0User | undefined
) => {
  const { t, i18n } = useTranslation();
  const checkMaintenanceMode = useMaintenanceModeCheck();
  const { addLog } = useLogger();
  const [basicInfo] = useBasicInfo();
  errorMessages = { ...errorMessages, ...getLoginErrorMessages(t) };
  const { addError, purgeNotifications } = useNotifications();
  const {
    user,
    setUser,
    clearUser,
    refetch,
    logout,
    setProcessingLogin,
  } = useAppConfig();

  const { refetch: domuiRefetch } = useDomuiAppConfig();
  // const client = useApolloClient() as ApolloClient<InMemoryCache>;
  const checkExternalDestination = checkExternalDestinationCurried(i18n);
  const handleLoginDestination = prepHandleLoginDestination(
    checkExternalDestination,
    addError,
    destinationOrPath,
    isUrlAbsolute,
    errorMessages,
    appUrl
  );
  const handleDominoLoginDestination = preHandleDominoLoginDestination(
    checkExternalDestination,
    addError,
    destinationOrPath,
    isUrlAbsolute,
    errorMessages,
    appUrl
  );
  const { fetchDominoMetaConfig } = useFetchDominoMetaConfig();
  const { fetchBasicInfo } = useFetchBasicInfo();
  const isAuth0LoginEnabled = isEnabled(FEATURE_AUTH0_LOGIN);
  // const langcode = i18n.language;
  const { addInfo, addSuccess } = useNotifications();
  const [createIndividualFromSession] = useCreateIndividualFromSession();
  const [auth0AccountSynced, setAuth0AccountSynced] = useState(false);
  const [auth0SuccessfullyLogin, setAuth0SuccessfullyLogin] = useState(false);
  const { logout: auth0Logout } = useAuth0();
  const auth0IndividualID =
    idTokenClaims?.['https://rotary.org/individualId'] || '';
  let createIndividualResponse: CreateIndividualFromSessionResponse | undefined;
  // Having React Hook Issue in below code, will look into it.
  // const {
  //   data: languages,
  //   loading: languagesLoading,
  //   // error: languagesError,
  //   fetchLanguages,
  // } = useFetchLanguages();

  // useEffect(() => {
  //   fetchLanguages();
  // }, []);

  // if (languagesLoading || !languages) {
  //   return <Loading />;
  // }
  const shownRef = useRef(false);
  const hasShown = sessionStorage.getItem('auth0-welcome');

  useEffect(() => {
    if (accessToken) {
      if (
        !auth0AccountSynced &&
        !auth0IndividualID &&
        !shownRef.current &&
        !hasShown
      ) {
        setTimeout(() => {
          addInfo(
            t(
              'auth0-pending-registration.info-notification',
              'Welcome to My Rotary!'
            ),
            { id: 'auth0-welcome' }
          );
        }, 5000);
        shownRef.current = true;
        sessionStorage.setItem('auth0-welcome', 'true');
      }

      if (auth0IndividualID && !auth0AccountSynced) {
        setAuth0AccountSynced(true);
      }

      // For existing users with individualId, mark them as synced immediately
      if (auth0IndividualID && !auth0AccountSynced) {
        setAuth0AccountSynced(true);
      }
    }
  }, [auth0IndividualID, auth0AccountSynced, accessToken]);
  const renderLoginErrorMessage = (message?: string): void => {
    clearUser();
    if (message) {
      purgeNotifications();
      addError(message);
    }
  };
  // This is a stripped version of the example. To checkout more of what you can
  // do with the widget: https://developer.okta.com/quickstart-fragments/widget/default-example/
  const handleSuccess = async (
    { idToken, accessToken }: SuccessData,
    renderSignInWidget: () => void
  ) => {
    // setTokens({
    //   idToken,
    //   accessToken,
    // });

    // Get first the access and the id tokens.
    try {
      setProcessingLogin(true);
      const dominoLogin = await fetchDominoMetaConfig(DOMINO_LOGIN_FLAG);
      const dominoRoute = await fetchDominoMetaConfig(DOMINO_ROUTE_FLAG);

      const dominoLoginFlag =
        String(dominoLogin[DOMINO_LOGIN_FLAG])?.toLowerCase() === 'true';
      const dominoRouteFlag =
        String(dominoRoute[DOMINO_ROUTE_FLAG])?.toLowerCase() === 'true';

      const { login } = user || {};
      const email = login || idToken?.email;

      if (typeof idToken.name === 'string') {
        const [firstName, lastName] = idToken.name.split(' ');
        setUser({
          login: login || idToken.email || '',
          isLoggedIn: true,
          attributes: {
            firstName,
            lastName,
          },
        });
      }

      const backend = await authenticateUserWebApp(
        accessToken.accessToken,
        idToken,
        addLog,
        {
          initialCall: 'Login',
          email,
          id: user?.id,
        }
      )();

      const authControllerEnabled = isEnabled(FEATURE_LOGIN_AUTH_CONTROLLER);
      let userInfo = null;
      let drupal: DrupalResponse = Status.OK;
      const {
        isOktaIndividualId,
        redirect,
        status,
      } = backend as UserAuthenticate;

      if (redirect) {
        window.localStorage.setItem('destination', redirect);
      }
      if (status !== Status.OK) {
        drupal = status || Status.CANNOT_AUTH_USER;
      } else if (isOktaIndividualId) {
        if (authControllerEnabled) {
          if (dominoLoginFlag) {
            const basicUserInfo = await fetchBasicInfo(email || '');
            userInfo = (basicUserInfo as unknown) as DIS.SSOTicketResponse;
          } else {
            const basicUserInfo = await basicInfo({
              variables: {
                emailId: email,
              },
            });
            userInfo = (basicUserInfo?.data
              ?.basicInfo as unknown) as DIS.SSOTicketResponse;
          }
        }

        addLog({
          level: LogLevel.Info,
          message: `Login::userInfo: ${JSON.stringify(userInfo)}`,
        });
        if (userInfo === null && authControllerEnabled) {
          drupal = Status.AUTH_CONTROLLER_ERROR;
          window.localStorage.setItem('destination', '');
        } else {
          // When FEATURE_LOGIN_AUTH_CONTROLLER=true, Drupal ignores idToken
          // entirely and uses only basicUserInfo (DIS data). Auth0 raw idToken
          // is safe to pass — it will not be validated by Drupal.
          drupal = await authenticateUserBackendApp(
            i18n.language,
            idTokenClaims?.__raw || '',
            addLog,
            {
              initialCall: 'Login',
              email,
              id: user?.id,
            },
            userInfo
          )();
        }
      }
      // MRX-1118 If the Okta account is not yet mapped to a DIS
      // individual, we should not try to authenticate the user in Drupal.
      // This will be done later, after the account mapping is finished.
      window.localStorage.setItem('idToken', idTokenClaims?.__raw || '');

      // Will do status calculation after making individual call to WebApp and DrupalApp
      if (drupal === Status.OK) {
        // Track a successful login
        // TODO if we need additional user data, we'll have to move this till after
        //  the useGetAnalyticsIndividualQuery hook is called in the auth hook
        // tell Adobe Launch that the user is now logged in
        window.rotaryDDO.userData.loginStatus = 'logged in';

        if (isOktaIndividualId && authControllerEnabled) {
          axios
            .get(
              `${process.env.GATSBY_BACKEND_APP_BASE_URL}/${i18n.language}/restapi/account/update`,
              { withCredentials: true }
            )
            .then(response => {
              addLog({
                level: LogLevel.Info,
                message: `Response from restapi/account/update: ${response.data}`,
              });
            })
            .catch(error => {
              addLog({
                level: LogLevel.Error,
                message: `Error from restapi/account/update: ${
                  (error as AxiosError).message
                }: ${(error as AxiosError).stack}`,
              });
            });
        }

        if (dominoRouteFlag) {
          // Display welcome message on Auth0 Registration sync is pending
          setAuth0AccountSynced(true);

          addLog({
            level: LogLevel.Info,
            message: `useAuth0Hook:: dominoRouteFlag auth0AccountSynced: ${auth0AccountSynced}`,
          });

          handleDominoLoginDestination();
          domuiRefetch();
        } else {
          addLog({
            level: LogLevel.Info,
            message: `useAuth0Hook:: auth0AccountSynced: ${auth0AccountSynced}`,
          });
          handleLoginDestination();
          refetch();
        }
      } else if (
        [
          Status.DATE_INCOMPATIBLE,
          Status.RENEW_ACCOUNT,
          Status.CANNOT_AUTH_USER,
        ].includes(status as Status)
      ) {
        // clearTokens();
        renderLoginErrorMessage(getMessageByLoginStatus(t, status as Status));
        renderSignInWidget();
        return;
      } else if ([Status.AUTH_CONTROLLER_ERROR].includes(drupal as Status)) {
        const {
          error_code: errorCode,
          error_message: errorMessage,
        } = (status as unknown) as DrupalError;
        addLog(getAuthenticationError(errorMessage, errorCode));
        // clearTokens();
        logout();
        renderLoginErrorMessage(
          getMessageByLoginStatus(t, Status.AUTH_CONTROLLER_ERROR)
        );
        return;
      } else {
        // If we get here, something is wrong with the authentication. We
        // should have the error code and error messages available in the
        // result. In this case, we just re-render the sign in widget and
        // display an error.
        const {
          error_code: errorCode,
          error_message: errorMessage,
        } = (status as unknown) as DrupalError;
        addLog(getAuthenticationError(errorMessage, errorCode));

        // clearTokens();
        renderLoginErrorMessage(
          getMessageByLoginStatus(t, Status.CANNOT_AUTH_USER)
        );
        renderSignInWidget();
      }
    } catch (e) {
      // clearTokens();

      if ((e as Error).message === OktaTokenErrors.JWT_ISSUED_IN_FUTURE) {
        renderLoginErrorMessage(
          getMessageByLoginStatus(t, Status.DATE_INCOMPATIBLE)
        );
        renderSignInWidget();
        return;
      }
      addLog({
        level: LogLevel.Error,
        message: (e as Error).message,
      });
      localizedNavigate('/error');
    } finally {
      setProcessingLogin(false);
      setAuth0SuccessfullyLogin(true);
      localStorage.setItem('auth0_login_completed', 'true');
    }
  };
  // If Auth0 Account does not have individualId, Follow Registration Flow
  useEffect(() => {
    // Skip if login redirect was already processed (prevents re-running after navigation)
    // Check both window flag and localStorage for persistence across page loads and tabs
    const hasProcessedRedirect =
      window.__destinationRedirectProcessed ||
      localStorage.getItem('auth0_login_processed') === 'true';

    if (hasProcessedRedirect) {
      // Ensure window flag is set if it was cleared
      if (!window.__destinationRedirectProcessed) {
        window.__destinationRedirectProcessed = true;
      }
      return;
    }

    if (
      isAuth0LoginEnabled &&
      accessToken &&
      // eslint-disable-next-line no-underscore-dangle
      idTokenClaims?.__raw &&
      auth0User?.sub
    ) {
      const handleRegistrationAndLogin = async () => {
        addLog({
          level: LogLevel.Info,
          message: `useAuth0Hook:: Auth0 Proceed with Registration Flow ...: ${auth0IndividualID}`,
        });
        try {
          if (!auth0IndividualID) {
            addLog({
              level: LogLevel.Info,
              message: `useAuth0Hook:: Auth0 Proceed with Registration Flow ...: ${auth0IndividualID}`,
            });
            const auth0Id = auth0User.sub;
            createIndividualResponse = await createIndividualFromSession({
              variables: {
                // Having React Hook Issue in below code, will look into it.
                // disLangcode: languages?.filter(
                //   ({ shortCode }: { shortCode: string }) => shortCode === langcode
                // )[0]?.id,
                disLangcode: 'eng', // Hooks Issue, Fix it later
                auth0Id,
              },
            });
            const successMessage = t(
              'account-mapping.create-individual-from-session.success',
              'Success! Your My Rotary account is now setup.'
            );

            let userInfo = null;
            const authControllerEnabled = isEnabled(
              FEATURE_LOGIN_AUTH_CONTROLLER
            );
            if (authControllerEnabled) {
              const emailId = (user as User)?.login;
              addLog({
                level: LogLevel.Info,
                message: `useAuth0Hook::emailId: ${emailId}`,
              });
              if (emailId) {
                const dominoLoginFlag = await fetchDominoMetaConfig(
                  DOMINO_LOGIN_FLAG
                );
                if (dominoLoginFlag) {
                  const basicUserInfo = await fetchBasicInfo(emailId);
                  userInfo = (basicUserInfo as unknown) as DIS.SSOTicketResponse;
                  // const basicUserInfo = await basicInfo({
                  //   variables: { emailId },
                  // });

                  // userInfo = (basicUserInfo?.data
                  //   ?.basicInfo as unknown) as DIS.SSOTicketResponse;
                } else {
                  const basicUserInfo = await basicInfo({
                    variables: { emailId },
                  });

                  userInfo = (basicUserInfo?.data
                    ?.basicInfo as unknown) as DIS.SSOTicketResponse;
                }
              }
            }
            addLog({
              level: LogLevel.Info,
              message: `useAuth0Hook:: fetch User Info: ${
                userInfo ? JSON.stringify(userInfo) : 'null'
              }`,
            });

            // drupalAuthentication(
            //   successMessage,
            //   addLog,
            //   user,
            //   i18n,
            //   addSuccess,
            //   userInfo
            // );
            // refetch();
            // localizedNavigate('/domui');
            addSuccess(successMessage);

            // There should be more code here on success message, Observe select member status page
          }
          addLog({
            level: LogLevel.Info,
            message: `$$$$$$$$ useAuth0Hook::User Info response $$$$$$$$: ${
              createIndividualResponse
                ? JSON.stringify(createIndividualResponse)
                : 'null'
            }`,
          });
          const individualAfterRegistration =
            createIndividualResponse?.data?.createIndividualFromSession;

          const individualIdAfterRegistration =
            individualAfterRegistration?.individualId;
          const memberIdAfterRegistration =
            individualAfterRegistration?.MemberID;
          const userGuidAfterRegistration =
            individualAfterRegistration?.USERGUID;
          const emailIdIdAfterRegistration =
            individualAfterRegistration?.emailId;

          // User Got created in Auth0 and Domino DB, Proceed with Login Flow
          const auth0AccessToken: Auth0AccessToken = {
            accessToken,
          };

          addLog({
            level: LogLevel.Info,
            message: `useAuth0Hook::idTokenClaims: ${
              idTokenClaims ? JSON.stringify(idTokenClaims) : 'null'
            }`,
          });
          const auth0IdTokens: Auth0IDToken = {
            // eslint-disable-next-line no-underscore-dangle
            idToken: idTokenClaims?.__raw || '',
            email: auth0IndividualID
              ? idTokenClaims?.['https://rotary.org/originalEmail'] ||
                idTokenClaims?.email
              : emailIdIdAfterRegistration,
            individualId:
              idTokenClaims?.['https://rotary.org/individualId'] ||
              individualIdAfterRegistration,
            MemberID:
              idTokenClaims?.['https://rotary.org/MemberID'] ||
              memberIdAfterRegistration,
            USERGUID:
              idTokenClaims?.['https://rotary.org/USERGUID'] ||
              userGuidAfterRegistration,
            name: `${idTokenClaims?.given_name} ${idTokenClaims?.family_name}`,
            user: auth0User,
          };

          addLog({
            level: LogLevel.Info,
            message: `useAuth0Hook::auth0IdTokens: ${
              auth0IdTokens ? JSON.stringify(auth0IdTokens) : 'null'
            }`,
          });

          // If IndividualId is not available, Some thing messed up during registration
          if (!auth0IdTokens.email || !auth0IdTokens.individualId) {
            addLog({
              level: LogLevel.Info,
              message: `useAuth0Hook:: Partial Login State, Logging out Auth0 ${auth0IdTokens.email} ${auth0IdTokens.individualId}`,
            });
            renderLoginErrorMessage(
              getMessageByLoginStatus(t, Status.CANNOT_AUTH_USER)
            );

            // This is a partial Auth0 Login State, Logout properly
            auth0Logout({ logoutParams: { returnTo: window.location.origin } });
            return;
          }

          const authData: SuccessData = {
            idToken: auth0IdTokens,
            accessToken: auth0AccessToken,
          };

          addLog({
            level: LogLevel.Info,
            message: `useAuth0Hook:: Auth0 Proceed with Login Flow, Scan Auth0 data before proceeding ...: ${JSON.stringify(
              authData
            )}`,
          });
          await handleSuccess(authData, () =>
            addLog({
              level: LogLevel.Info,
              message: 'Auth0 renderSignInWidget shell',
            })
          );
        } catch (err) {
          addLog({
            level: LogLevel.Error,
            message: `Failed to register Auth0 new account:${JSON.stringify(
              err
            )}`,
          });
        }
      };
      handleRegistrationAndLogin();
    }
  }, [
    isAuth0LoginEnabled,
    accessToken,
    // eslint-disable-next-line no-underscore-dangle
    idTokenClaims?.__raw,
    auth0User?.sub,
    auth0IndividualID,
  ]);

  useEffect(() => {
    if (isAuth0LoginEnabled) {
      const mappingError = localStorage.getItem(MAPPING_NOT_AVAILABLE_ERROR);

      if (mappingError) {
        addError(mappingError);
        localStorage.removeItem(MAPPING_NOT_AVAILABLE_ERROR);
      }
    }
  }, [isAuth0LoginEnabled]);

  const languageAvailable = isLanguageAvailable();

  const isCorrectClientDate = useCallback(async () => {
    const clientDate = moment().toISOString();

    addLog({
      level: LogLevel.Info,
      message: `[Time Check] Client date is ${clientDate}`,
    });

    const { data } = await compareClientServerTime(clientDate);
    addLog({
      level: LogLevel.Info,
      message: `[Time Check] Result is ${JSON.stringify(data)}`,
    });
  }, []);

  useEffect(() => {
    if (isAuth0LoginEnabled) {
      isCorrectClientDate();
    }
  }, [isAuth0LoginEnabled]);

  useEffect(() => {
    if (isAuth0LoginEnabled && languageAvailable) {
      // Set the destination in the local storage, but only if we did not
      // already process the redirect.
      // @tooo: Make this more generic maybe.
      if (
        typeof window.__destinationRedirectProcessed === 'undefined' ||
        !window.__destinationRedirectProcessed
      ) {
        // Remove a potential leftover destination from the local storage. But
        // make sure to not do this if we have Okta tokens in the hash.
        // Otherwise we remove the correct destination before it is used.
        if (!window.location.hash) {
          window.localStorage.removeItem('destination');
        }

        const queryParams = queryString.parse(window.location.search);
        if (
          queryParams.destination &&
          typeof queryParams.destination === 'string'
        ) {
          window.localStorage.setItem('destination', queryParams.destination);
        }
        if (
          queryParams.ph_return_path &&
          typeof queryParams.ph_return_path === 'string'
        ) {
          window.localStorage.setItem(
            'destination',
            `/member-center/rotary-global-rewards/offers#${queryParams.ph_return_path}`
          );
        }
      }
    }
  }, [
    isAuth0LoginEnabled,
    i18n.language,
    languageAvailable,
    checkMaintenanceMode?.data?.maintenanace,
  ]);

  const isMaintenanceMode = checkMaintenanceMode?.data?.maintenanace === 1;

  return { auth0AccountSynced, auth0SuccessfullyLogin, isMaintenanceMode };
};

export default useAuth0Hook;
